// @ts-strict-ignore
import { LMLoggerService } from '@services/lm-logger.service';
import { LMModelField } from './lm-model-field';
import { LMModelServiceAdapter } from './lm-model-service-adapter';
import { union } from 'lodash-es';
import { LMModelState } from './lm-model-state';
import { Observable, Subject } from 'rxjs';
import { AppConstants, LMTransformOptions } from '@utils/index';
import { LMPanelTransactionEvent } from '@form/services';
import { LMAdHocSelectedField } from '@app/components';

export type LMModelFields = { [ key: string ]: LMModelField };
export type LMModelScannableFields = { [ key: string ]: [LMModelField] };
export type LMModelMultiPartFields = { [ key: string ]: File | Blob };
export type LMModelXLMultiPartFields = LMModelMultiPartFields;

export interface LMModelCache {
  dirtyFields: LMDirtyFields;
  errorFields: LMErrorFields;
  modelFields: LMModelFields;
  autoApply: boolean;
}

export class LMModelChangedEvent implements LMModelEvent {
  eventType: LMModelEventType;
  source?: LMModelSourceEvent;
  info?: LMModelFieldInfo;

  constructor(eventType: LMModelEventType, source?: LMModelSourceEvent, info?: LMModelFieldInfo) {
    this.eventType = eventType;
    this.source = source;
    this.info = info;
  }
}

export class LMModelStatusEvent implements LMModelEvent {
  eventType: LMModelEventType;
  isDirty: boolean;
  hasError: boolean;
  cause?: LMModelStatusCause;

  constructor(isDirty: boolean, hasError: boolean, cause?: LMModelStatusCause) {
    this.eventType = 'status';
    this.isDirty = isDirty;
    this.hasError = hasError;
    this.cause = cause;
  }
}

export class LMTimeZoneChangedEvent {
  bindId: string;
  timeZone: string;
  previousTimeZone?: string;
  subscribingBindIds?: string[];
}

export class LMXLFileUploadEvent {
  dataView: LMDataView;
  fieldId: string;
  uploadEventType: 'START_UPLOAD' | 'COMPLETE_UPLOAD'| 'COMPLETE_UPLOAD_ERROR'| 'PART_UPLOAD' | 'PART_UPLOAD_ERROR';
  newResourceId?: string;
  errorMsg?: string;
}

export class LMModel {
  private _fields: LMModelFields;
  private _manualrepfields: LMModelFields = {};
  private _specFields: LMSpecFields;
  private _dataView: LMDataView;
  private _modelState: LMModelState;
  private _modelChanged$ = new Subject<LMModelChangedEvent>();
  private _scannableNamedfields: LMModelScannableFields = {};
  private lmLoggerService: LMLoggerService;
  private _numberLabel: string;
  private _timeZoneChangedSource$ = new Subject<LMTimeZoneChangedEvent>();
  readonly instanceId: string;
  private _xlFileUploadSubject$ = new Subject<LMXLFileUploadEvent>();
  private _isInOverlay: boolean;
  private _adhocSearchFields: LMAdHocSelectedField[] = [];

  timeZoneChangedEvent$ = this._timeZoneChangedSource$.asObservable();

  constructor(
    public lmModelServiceAdapter: LMModelServiceAdapter,
    private lmModelOptions: LMModelOptions
  ) {
    // a shortcut reference
    this.lmLoggerService = lmModelServiceAdapter.lmLoggerService;
    this.instanceId = this.lmLoggerService.instanceId;

    this._modelState = new LMModelState(lmModelServiceAdapter);
  }

  public setAdhocSearchFields(fields: LMAdHocSelectedField[]) {
    this._adhocSearchFields = fields;
  }

  /**
   * Returns the list of adhoc search field IDs that have been added to this model.
   */
  public getAdhocSearchFieldIds(): LMAdHocSelectedField[] {
    return this._adhocSearchFields;
  }

  // private methods
  private _applyDirtyState(incomingModel: LMModel) {
    // going to the sub-form or to a compact group form
    const dataView = incomingModel.getData();
    const fields = dataView ? dataView.fields : incomingModel.state.dirtyFields;

    // need to iterate all field because the model.state.dirtyFields
    // does not include fields not in the layout. LMCLIENT-38786
    // TODO: consider adding non layout fields to the model.state.dirtyField collection.
    Object.keys(fields).forEach((key) => {
      const incomingField = incomingModel.getField(key);

      if (!incomingField || !incomingField?.evaluateIsDirty) {
        return;
      }

      const incomingSpecField: LMSpecField = incomingModel._specFields[ key ];
      const currentField = this.getField(key);
      const currentSpecField: LMSpecField = this._specFields[ key ];

      if (currentField) {
        const incomingValue = incomingField.value;

        if (currentSpecField && currentSpecField.isMultiselect) {
          // re-apply the selected values and to the stateValues
          currentField.value = incomingField.value;
          currentField.states = this.lmModelServiceAdapter.fieldStatesToMultiOptionsArray(incomingField.states, incomingField.value);

        } else {
          // need to apply the value here
          // LMCLIENT-24650: if incoming field is not on the parent form, but is on this form, then
          // use currentSpecField and format the display value
          if (currentSpecField && (!incomingSpecField || (incomingSpecField && incomingSpecField.generated))) {
            const options: LMTransformOptions = {
              precision:       incomingField.precision,
              isCompoundField: incomingField.isCompoundField
            };
            currentField.value = this.lmModelServiceAdapter.toDisplayValue(incomingField.value, currentSpecField, options);

          } else {
            // value is already transformed or if currentSpecField is undefined, meaning the field is not on the form,
            // value could be in Landmark format
            currentField.value = incomingField.value;
          }

          if (currentSpecField && currentSpecField.hasUserDefinedStates && incomingField.states !== undefined) {
            // for user-defined state fields, need to set the selected value back onto the stateValues property
            currentField.states = incomingField.states;
          }
        }

        currentField.applyState.next(true);
      }

      this.setDirty(key, incomingModel.state.dirtyFields[ key ]);
    });
  }

  private _applyDirtyStateFromForm(incomingModel: LMModel, bindIds: Array<string>) {
    // returning from a sub-form

    const currentDirtyFields = Object.keys(this.state.dirtyFields);
    const incomingDirtyFields = Object.keys(incomingModel.state.dirtyFields);

    // create a union of unique bindIds
    const allFields = union(bindIds, incomingDirtyFields, currentDirtyFields);

    allFields.forEach((key) => {
      const currentContainsKey = currentDirtyFields.includes(key);
      const incomingContainsKey = incomingDirtyFields.includes(key);

      // SPECIAL CASE: if both are true and is a multi-select field,
      // need to re-apply the value and statesValues from the incoming model
      if (currentContainsKey === true && incomingContainsKey === true) {
        const specField: LMSpecField = this._specFields[ key ];

        if (specField && specField.isMultiselect) {
          const currentField = this.getField(key);
          const incomingField = incomingModel.getField(key);

          // re-apply the selected values to the stateValues
          currentField.value = incomingField.value;
          currentField.states = this.lmModelServiceAdapter.fieldStatesToMultiOptionsArray(incomingField.states, incomingField.value);
        }

        // no need to notify listeners as both are dirty
      }

      // otherwise, if both are not equal, then a change has occurred between forms
      // and the state needs to be applied appropriately
      if (currentContainsKey !== incomingContainsKey) {
        let isDirty = true;
        const currentField = this.getField(key);

        if (currentField) {
          // SPECIAL CASE: if a multi-select field, need to re-apply the statesValues from the incoming model
          const specField: LMSpecField = this._specFields[ key ];
          const incomingField = incomingModel.getField(key);

          if (specField && specField.isMultiselect) {
            // re-apply the selected values to the stateValues
            currentField.states = this.lmModelServiceAdapter.fieldStatesToMultiOptionsArray(incomingField.states, incomingField.value);
          }

          // do not need to set the value because the incoming dataView values were applied via updateData()
          currentField.applyState.next(true);
        }

        if (currentContainsKey) {
          isDirty = false;
        }

        this.setDirty(key, isDirty);
      }
    });
  }

  private _applyErrorState(incomingModel: LMModel) {
    // TODO: need to refactor due to changes in sohoxi controls
    Object.keys(incomingModel.state.errorFields).forEach((key) => {
      const incomingError = incomingModel.state.errorFields[ key ];

      this.setFieldError(key, incomingError.message, false);
      this.setError(key, incomingError.hasError, incomingError.message);
    });
  }

  private _populateModel(specFields: LMSpecFields): LMModelFields {
    const fields: LMModelFields = {};

    Object.keys(specFields).forEach((key) => {
      fields[ key ] = new LMModelField(specFields[ key ], this);

      // build map of scannable fields
      if (fields[ key ].scannable && fields[ key ].scannableName) {
        if (this._scannableNamedfields[fields[key].scannableName]) {
          this._scannableNamedfields[fields[key].scannableName].push(fields[ key ]);
        } else {
          this._scannableNamedfields[fields[key].scannableName] = [ (fields[ key ]) ];
        }
      }
    });
    return fields;
  }

  private _processMemberValue(value: any, dvField: LMField): any {
    // update the individual dataView member values from the parent value array
    if (dvField.members) {
      for (let index = 0, len = dvField.members.length; index < len; index++) {
        const memberId = dvField.members[ index ];
        const dvMember = this._dataView.fields[ memberId ];

        if (dvMember) {
          if (value !== null && value[ index ] !== undefined) {
            dvMember.value = value[ index ];

          } else {
            dvMember.value = '';
          }

          // need to keep the corresponding modelField value in sync
          // as model can be passed
          const modelMember = this.getField(memberId);
          if (modelMember) {
            modelMember.value = dvMember.value;
          }
        }
      }
    }
    return value;
  }

  // for multi-select, sets the selected stateValue onto each member dataview field
  private _processMultiSelectValues(dvField: LMField, specField: LMSpecField, modelField: LMModelField) {
    const dvFields = this._dataView.fields;

    if (modelField && modelField.states && modelField.members) {

      modelField.members.forEach( key => {
        const dvMemberField = dvFields[ key ];

        // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero, etc
        // eslint-disable-next-line eqeqeq
        const state = modelField.states.filter(item => item.value == dvMemberField.value);

        if (state && state.length > 0) {
          dvMemberField.stateValues = state;
        }
      });
    }
  }

  // for user-defined states, sets the current dataView field value back onto the dataView field stateValues property
  private _processUserDefinedStateValue(dvField: LMField, specField: LMSpecField, modelField?: LMModelField) {
    // if dataView view field stateValue equals current value, no need to change
    // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero, etc
    // eslint-disable-next-line eqeqeq
    if (dvField.stateValues && dvField.stateValues[ 0 ].value == dvField.value) {
      return;
    }

    // dataView field value was not the same, so find value in the model field
    if (modelField && modelField.states) {
      for (let index = 0, len = modelField.states.length; index < len; index++) {
        const state: LMStateValue = modelField.states[ index ];

        // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero, etc
        // eslint-disable-next-line eqeqeq
        if (state.value == dvField.value) {
          dvField.stateValues = [ state ] as Array<LMStateValue>;
          return;
        }
      }
    }

    // safety: shouldn't get here, but value cannot be found so create a default
    let label = dvField.value;

    // need to set the label value to an empty string for BCD, NUMERIC, and PERCENT types when
    // the value evaluates to a zero as it is the blank entry for a user-defined state
    if (specField.type === AppConstants.BCD
      || specField.type === AppConstants.NUMERIC
      || specField.type === AppConstants.PERCENT) {
      // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero
      // eslint-disable-next-line eqeqeq
      if (dvField.value == 0) {
        label = '';
      }
    }

    return [
      {
        value: dvField.value,
        label
      }
    ] as Array<LMStateValue>;
  }

  private _setFields(dvFields: LMFields, opts?: LMModelSetFieldsOptions) {
    Object.keys(dvFields).forEach((key) => {
      let field: LMModelField = this.getField(key);

      // if there is a dataView field, but not a specField,
      // add a model field for processing functionality; not bound to a component
      if (!field) {
        field = this.getDefaultField(key);
        this._fields[ key ] = field;
      }

      field.setData(dvFields[ key ], this._dataView.fields, opts);

      // checkDirtyState is set by this.setFields which is called by the result of LMFormComponent.fireInitialValueRules()
      // or by LMFormContentStepProcessComponent.updateModelAfterTransaction()
      if (opts && opts.checkDirtyState) {
        // in LMFieldBaseComponent, in combination with the model.changed event, this will cause a process in ngAfterViewChecked
        // to be executed to evaluate the whether the field is dirty
        field.applyState.next(true);
      }
    });
  }

  public updateLazyConditionFields(dvFields: LMFields) {
    Object.keys(dvFields).forEach((key) => {
      if (!this._dataView.fields[key]) {    // && this._dataView.fields[key].value === undefined
        let modelField: LMModelField = this.getField(key);
        if (!modelField) {
          // console.log('#### LazyDashboardConditions: cannot find model field');
          modelField = this.getFieldSpecForLazyConditionField(key, dvFields[ key ]);
          this._fields[ key ] = modelField;
        }

        this._dataView.fields[ key ] = dvFields[ key ];
        modelField.setData(dvFields[ key ], this._dataView.fields);
      }
    });
  }

  private getFieldSpecForLazyConditionField(bindId: string, dvField: LMField): LMModelField {
    const specField: LMSpecField = {
      bindId,
      type:      dvField.type ? dvField.type : '',
      label:     null,
      generated: true
    };

    const modelField: LMModelField = new LMModelField(specField, this);
    modelField.value = '';

    return modelField;
  }

  public updateAdhocSearchFields(searchFields: LMSpecFields, dvFields: LMFields) {
    Object.keys(searchFields).forEach((key) => {
      if (!this._dataView.fields[key]) {    // && this._dataView.fields[key].value === undefined
        let modelField: LMModelField = this.getField(key);
        if (!modelField) {
          // console.log('#### LazyDashboardConditions: cannot find model field');
          modelField = this.getFieldSpecForAdhocSearchField(searchFields[ key ]);
          this._fields[ key ] = modelField;
        }

        this._dataView.fields[ key ] = dvFields[ key ];
        modelField.setData(dvFields[ key ], this._dataView.fields);
      }
    });
  }

  private getFieldSpecForAdhocSearchField(specField: LMSpecField): LMModelField {
    const modelField: LMModelField = new LMModelField(specField, this);
    modelField.value = '';

    return modelField;
  }


  timeZoneChanged(event: LMTimeZoneChangedEvent): void {
    this._timeZoneChangedSource$.next(event);
  }

  public addManualRepField(fieldId: string) {
    const manRepModelField: LMModelField = new LMModelField( this._specFields[ fieldId ], this);
    manRepModelField.isManualRepField = true;
    this._manualrepfields[fieldId] = manRepModelField;
  }

  // public getters
  public get action(): string {
    if (this._dataView) {
      return this._dataView.action;
    }
    return '';
  }

  public get contextProperties(): any {
    // TODO: need safety
    return this._dataView.contextProperties;
  }

  public get dirtyFields(): LMDirtyFields {
    return this._modelState.dirtyFields;
  }

  public get blockingErrorFields(): LMBlockingErrorFields {
    return this._modelState.blockingErrorFields;
  }

  public get initialValueFields(): Array<string> {
    return this._modelState.initialValueFields;
  }

  /**
   * Used by form transactions.
   * @return
   */
  public get formResourceId(): string {
    if (this._dataView) {
      return this.resourceId + '.' + this.formSpec.busFormSpec.name;
    }

    return '';
  }

  public set isInOverlay(value: boolean) {
    this._isInOverlay = value;
  }

  public get isInOverlay(): boolean {
    return this._isInOverlay;
  }

  public get hasError(): boolean {
    return this._modelState.hasError;
  }

  public get hasBlockingError(): boolean {
    return this._modelState.hasBlockingError;
  }

  public get hasUpdateAction(): boolean {
    return this.lmModelServiceAdapter.modelActionState.actionInfo.isUpdateAction;
  }

  public get globalCache(): any {
    return this._dataView.globalCache;
  }

  public get isCreateAction(): boolean {
    return this.lmModelServiceAdapter.modelActionState.actionInfo.isCreateAction;
  }

  public get isDirty(): boolean {
    return this._modelState.isDirty;
  }

  public get isFormMutable(): boolean {
    return this.lmModelServiceAdapter.isFormMutable;
  }

  public isDrillAroundMode(): boolean {
    return this.lmModelOptions.drillAroundMode;
  }

  public get isReadOnlyForm(): boolean {
    if (this.isDrillAroundMode()) {
      return true;
    }
    return this.lmModelServiceAdapter.isReadOnlyForm(this.lmModelOptions.formSpec);
  }

  public get isPageContextForm(): boolean {
    return this.lmModelServiceAdapter.isPageContextForm(this.lmModelOptions.formSpec);
  }

  public get isSearchForm(): boolean {
    return this.lmModelServiceAdapter.isSearchForm(this.lmModelOptions.formSpec);
  }

  public get isCubeSearchForm(): boolean {
    return this.lmModelServiceAdapter.isCubeSearchForm(this.lmModelOptions.formSpec);
  }

  public get isDetailFieldsForm(): boolean {
    return this.lmModelServiceAdapter.isDetailFieldsForm(this.lmModelOptions.formSpec);
  }

  public get hasManualRepFileds(): boolean {
    return (Object.keys(this._manualrepfields).length > 0);
  }

  public get formName(): string {
    return this.lmModelServiceAdapter.getFormName(this.lmModelOptions.formSpec);
  }

  public get formSpec(): LMFormSpec {
    return this.lmModelOptions.formSpec;
  }

  public get formUrlInfo(): LMFormUrlInfo {
    return this.lmModelOptions.formUrlInfo;
  }

  public get modelChanged(): Subject<LMModelChangedEvent> {
    return this._modelChanged$;
  }

  // for use internally by LMModel only
  protected get state(): LMModelState {
    return this._modelState;
  }

  public get statusChanges(): Subject<LMModelStatusEvent> {
    return this._modelState.statusChanges;
  }

  public get multiPartFields(): LMModelMultiPartFields {
    const multiPartFields: LMModelMultiPartFields = {};

    Object.keys(this._fields).forEach((key) => {
      const field: LMModelField = this.getField(key);

      if (field && field.multiPartFile && !field.xlUpload) {
        multiPartFields[ key ] = field.multiPartFile;
      }
    });

    return multiPartFields;
  }

  public get xlMultiPartFields(): LMModelXLMultiPartFields {
    const xlMultiPartFields: LMModelXLMultiPartFields = {};

    Object.keys(this._fields).forEach((key) => {
      const field: LMModelField = this.getField(key);

      if (field && field.multiPartFile && field.xlUpload) {
        xlMultiPartFields[ key ] = field.multiPartFile;
      }
    });

    return xlMultiPartFields;
  }

  public get xlUploadInprogressFields(): LMModelFields {
    const xlUploadInProgressFlds = {};
    Object.keys(this._fields).forEach((key) => {
      const field = this.getField(key);
      if (field && field.xlUpload && field.xlUploadId) {
        xlUploadInProgressFlds[ key ] = field;
      }
    });
    return xlUploadInProgressFlds;
  }

  public get xlFileUploadSubject(): Subject<LMXLFileUploadEvent> {
    return this._xlFileUploadSubject$;
  }

  public get numberLabel(): string {
    return this._numberLabel;
  }

  public set numberLabel(value: string) {
    this._numberLabel = value;
    this._modelChanged$.next(new LMModelChangedEvent('numberLabelChanged'));
  }

  public set scannableFieldsData(info: LMModelFieldInfo) {
    this._modelChanged$.next(new LMModelChangedEvent('scannableChanged', undefined, info));
  }

  public setModelFieldsFromScannableData(scannableReturnData: LMModelFieldInfo) {

    for (const setName of Object.keys(scannableReturnData)) {
      const val = scannableReturnData[setName];
      const processReturnField = this._scannableNamedfields[setName];
      if (processReturnField && processReturnField.length > 0) {
        for (const modelField of processReturnField) {
          modelField.value = val;
        }
      }
    }
  }
  /**
   * Used by independent panel and template forms.
   * @return
   */
  public get resourceId(): string {
    if (this._dataView) {
      if (this.isSearchForm) {
        return this._dataView.name;
      }

      return this._dataView.resourceId;
    }

    return '';
  }

  public get navigations(): Array<string> {
    if (this._dataView) {
      return this._dataView.navigations;
    }

    return [];
  }

  public evaluateAndApplyDirtyState(bindId: string): boolean {
    const modelField: LMModelField = this.getField(bindId);
    const isDirty = (modelField) ? modelField.evaluateIsDirty : false;
    this.setDirty(bindId, isDirty);
    return isDirty;
  }

  public isFieldDirty(bindId: string): boolean {
    return this._modelState.isFieldDirty(bindId);
  }

  public setDirty(bindId: string, isDirty: boolean) {
    this._modelState.setDirty(bindId, isDirty);
  }

  public setError(bindId: string, hasError: boolean, message?: string) {
    this._modelState.setError(bindId, hasError, message || '');
  }

  public setBlockTransactionError(bindId: string, hasError: boolean, invalidEvent: any) {
    this._modelState.setBlockTransactionError(bindId, hasError, invalidEvent);
  }

  // public methods
  public applyState(incomingModel: LMModel, bindIds?: Array<string>) {
    // need to process this down the stack so the sohoxi controls can catch up
    setTimeout(() => {
      this.lmLoggerService.time({ id: 'LMModel.applyState() ' + this.instanceId });

      if (bindIds) {
        this._applyDirtyStateFromForm(incomingModel, bindIds);

      } else {
        this._applyDirtyState(incomingModel);
      }

      this._applyErrorState(incomingModel);

      this._modelChanged$.next(new LMModelChangedEvent('update'));

      this.lmLoggerService.timeEnd('LMModel.applyState() ' + this.instanceId);
    }, 1);
  }

  public addFieldsFromModel(specFields: LMSpecFields, model: LMModel) {
    Object.keys(specFields).forEach((key) => {
      this._fields[ key ] = new LMModelField(specFields[ key ], this);
      this._fields[ key ].value = model.getField(specFields[ key ].bindId).value;
    });
  }

  public destroy() {
    // TODO: maf 08/08/2019 - issue here; cannot destroy model as will be needed on return of sub / compact form
    // this.lmLoggerService.log(['LMModel.destroy()', this.instanceId]);
    // this._modelState.destroy();
    // this._modelState = null;
    //
    // this._modelChanged$.unsubscribe();
    // this._modelChanged$ = null;
    //
    // Object.keys(this._fields).forEach((key) => {
    //   let field = this.getField(key);
    //
    //   if (field) {
    //     field.destroy();
    //   }
    // });
  }

  public updateModelFromGenAI(dataView: LMDataView, incomingModel: LMModel, fieldId: string, generatedText: string, fieldContents?: string) {
    const modelField = this.getField(fieldId);

    this.updateData(dataView);

    if(fieldContents) { // AI Rephrase
      modelField.value = fieldContents.replace('{{GenAIRephrase}}', generatedText.trim());
    } else { // AI Append
      modelField.value = modelField.value + generatedText;
    }
    modelField.applyState.next(true);

    this._modelChanged$.next(new LMModelChangedEvent('update', { type: 'aigenerate'}));
  }

  public updateModelFromCompactForm(dataView: LMDataView, incomingModel: LMModel, fieldId: string, bindIds?: Array<string>) {
    // fieldId is the compact group field id
    const modelField = this.getField(fieldId);

    this.updateData(dataView);

    if (!modelField.isNestedCompoundField) {
      // don't call the public applyState() here as the setTimeout cannot be executed
      this._applyDirtyStateFromForm(incomingModel, bindIds);
      this._applyErrorState(incomingModel);

    } else {
      // nested compound fields will call applyState() in lmModelServiceAdapter.parseGroupFieldMembers()
      this._applyErrorState(incomingModel);
    }

    // lmModelServiceAdapter.parseGroupFieldMembers() will apply the dirty state
    const value = this.lmModelServiceAdapter.computeCompactFieldValue(dataView, fieldId, this._specFields);

    // setting the value will cause LMModel.parseGroupFieldMembers() to be executed
    modelField.value = value;
    modelField.applyState.next(true);

    this._modelChanged$.next(new LMModelChangedEvent('update'));
  }

  public updateModelFromManualRepField(fieldId: string, value: any, notifyListeners?: boolean) {
    // fieldId is the compact group field id
    const modelField = this.getField(fieldId);


    // setting the value will cause LMModel.parseGroupFieldMembers() to be executed
    modelField.value = value;
    modelField.applyState.next(true);

    if (notifyListeners) {
      this._modelChanged$.next(new LMModelChangedEvent('update'));
    } else {
      this._modelChanged$.next(new LMModelChangedEvent('manualRepChanged'));
    }
  }

  public updateModelFromSubForm(dataView: LMDataView, incomingModel: LMModel, bindIds?: Array<string>) {
    this.updateData(dataView);
    this.applyState(incomingModel, bindIds);
  }

  public updateModelFromDataViewForm(bindId: string, value: string, incomingModel: LMModel) {
    const modelField: LMModelField = this.getField(bindId);

    modelField.value = value;
    this.evaluateAndApplyDirtyState(bindId);
    // const isDirty = this.evaluateAndApplyDirtyState(bindId);
    // this.setDirty(bindId, isDirty);
    modelField.applyState.next(true);

    this.applyState(incomingModel, [ bindId ]);
  }

  public getManuualRepFields(): LMModelFields {
    return this._manualrepfields;
  }

  public getPagingUrls(): LMFormPagingUrls {
    return (this._dataView && this._dataView.pagingUrls) ? this._dataView.pagingUrls : undefined;
  }

  public getData(): LMDataView {
    if (this.lmLoggerService.cskLog === 2) {
      this.lmLoggerService.time({
        id: `GetData-${this.instanceId}`,
        description: 'LMModel Get Data',
        functionName: 'getData()',
        className: 'LMModel'
      });
    }

    const dvFields = this._dataView ? this._dataView.fields : {};

    Object.keys(dvFields).forEach((key) => {
      const modelField: LMModelField = this.getField(key);
      const dvField: LMField = dvFields[ key ];
      const specField: LMSpecField = this._specFields[ key ];

      if (modelField) {
        if (specField && specField.isMultiselect) {
          dvField.value = this._processMemberValue(modelField.getData(), dvField);
          this._processMultiSelectValues(dvField, specField, modelField);

        } else {
          dvField.value = modelField.getData();

          if (specField && specField.hasUserDefinedStates) {
            // for user-defined state fields, need to set the selected value back onto the stateValues property
            this._processUserDefinedStateValue(dvField, specField, modelField);
          }
        }
      }
    });

    if (this.lmLoggerService.cskLog === 2) {
      this.lmLoggerService.timeEnd(`GetData-${this.instanceId}`);
    }

    return this._dataView;
  }

  public getOriginalData(): LMDataView {
    this.lmLoggerService.time({ id: 'LMModel.getOriginalData() ' + this.instanceId });
    const dvFields = this._dataView.fields;

    Object.keys(dvFields).forEach((key) => {
      const modelField: LMModelField = this.getField(key);
      const dvField: LMField = dvFields[ key ];
      const specField: LMSpecField = this._specFields[ key ];

      if (modelField) {
        if (specField && specField.isMultiselect) {
          // do not call _processMemberValue() here as the original value is just needed
          dvField.value = modelField.originalValue;

        } else {
          dvField.value = modelField.originalValue;

          if (specField && specField.hasUserDefinedStates) {
            // for user-defined state fields, need to set the selected value back onto the stateValues property
            this._processUserDefinedStateValue(dvField, specField, modelField);
          }
        }
      }
    });

    this.lmLoggerService.timeEnd('LMModel.getOriginalData() ' + this.instanceId);
    return this._dataView;
  }

  public getSkinnyData(): LMDataView {
    this.lmLoggerService.time({ id: 'LMModel.getSkinnyData() ' + this.instanceId });
    const dvFields = this._dataView.fields;
    const dataViewFields: LMFields = {};

    Object.keys(dvFields).forEach((key) => {
      const field: LMModelField = this.getField(key);
      const dvField = dvFields[ key ];

      if (field && dvField.hasOwnProperty('value')) {
        dataViewFields[ key ] = { value: field.getData() };
      }
    });

    const dataView: LMDataView = Object.assign({}, this._dataView);
    dataView.fields = dataViewFields;

    this.lmLoggerService.timeEnd('LMModel.getSkinnyData() ' + this.instanceId);
    return dataView;
  }

  public getDefaultField(bindId: string): LMModelField {
    const specField: LMSpecField = {
      bindId,
      type:      '',
      label:     null,
      generated: true
    };

    const modelField: LMModelField = new LMModelField(specField, this);
    modelField.value = '';

    return modelField;
  }

  public getField(bindId?: string): LMModelField {
    // if no bindId or bindId is not in the collection, return null
    // caller can execute getDefaultField() to use for binding
    if (!bindId
      || bindId.trim().length === 0
      || !this._fields.hasOwnProperty(bindId)) {
      return null;
    }

    return this._fields[ bindId ];
  }

  public populate(specFields: LMSpecFields) {
    this._specFields = specFields;
    this._fields = this._populateModel(this._specFields);
  }

  /**
   * Initial setting of model data, marking all fields as pristine (isDirty == false)
   * @param            newDataView
   * @param opts
   */
  public setData(newDataView: LMDataView, opts?: LMModelSetDataOptions) {

    if (this.lmLoggerService.cskLog === 2) {
      this.lmLoggerService.time({
        id: `SetData-${this.instanceId}`,
        description: 'LMModel Set Data',
        functionName: 'setData()',
        className: 'LMModel'
      });
    }

    // notifyListeners defaults to true if not passed
    const notifyListeners = (opts && typeof opts.notifyListeners === 'boolean') ? opts.notifyListeners : true;
    const resetData = (opts && opts.source && opts.source.type === 'paging');
    this.isInOverlay = (opts && typeof opts.isInOverlay === 'boolean') ? opts.isInOverlay : false;

    // reset data is set after a previous or next operation
    const currentDataView = (resetData) ? undefined : this._dataView;
    this._dataView = this.lmModelServiceAdapter.updateActionOnDataView(currentDataView, newDataView, this.lmModelOptions.formSpec);

    this.lmModelServiceAdapter.updateModelActionState(this._dataView, this.lmModelOptions.formSpec);

    // Leave resetData to true for both types of LMModelSourceEvent, paging and transaction.
    // The const flag resetData will be used only for setting the correct action in dataView above.
    const fieldOptions = { resetData: true } as LMModelSetFieldsOptions;
    if (opts?.filterCriteria && opts.filterCriteria.length > 0) {
      fieldOptions.filterCriteria = opts.filterCriteria;
      // since we are in the case of a search being clicked on a search form, we do NOT want to reset
      // the current data
      fieldOptions.resetData = false;
    }

    if (opts?.alphaDefaultFilterCondition) {
      fieldOptions.alphaDefaultFilterCondition = opts.alphaDefaultFilterCondition;
    }

    if (opts?.isAdHocImplicit) {
      fieldOptions.suppressRequired = true;
    }

    this._setFields(this._dataView.fields, fieldOptions);

    this._modelState.clearAll();

    if (notifyListeners) {
      const source = (opts && opts.source) ? opts.source : undefined;
      this._modelChanged$.next(new LMModelChangedEvent('set', source));
    }

    if (this.lmLoggerService.cskLog === 2) {
      this.lmLoggerService.timeEnd(`SetData-${this.instanceId}`, [ 'options', opts ]);
    }
  }

  /**
   * Updates model data, keeping fields in the current state
   * @param dataView
   * @param opts
   */
  public updateData(dataView: LMDataView, opts?: {
    notifyListeners?: boolean;
  }) {
    this.lmLoggerService.time({ id: 'LMModel.updateData() ' + this.instanceId });

    // notifyListeners defaults to true if not passed
    const notifyListeners = (opts && typeof opts.notifyListeners === 'boolean') ? opts.notifyListeners : true;

    // TODO: need to save incoming dataView to this._dataView ?

    this.lmModelServiceAdapter.updateModelActionState(dataView, this.lmModelOptions.formSpec);

    this._setFields(dataView.fields);

    if (notifyListeners) {
      this._modelChanged$.next(new LMModelChangedEvent('update'));
    }

    this.lmLoggerService.timeEnd('LMModel.updateData() ' + this.instanceId, [ 'options', opts ]);
  }

  public setFields(dvFields: LMFields, opts?: {
    notifyListeners?: boolean;
    source?: LMPanelTransactionEvent;
    initialValue?: boolean
  }): Observable<LMModelChangedEvent> {
    return new Observable<LMModelChangedEvent>(observer => {
      this.lmLoggerService.time({ id: 'LMModel.setFields() ' + this.instanceId });

      // notifyListeners defaults to true if not passed
      const notifyListeners = (opts && typeof opts.notifyListeners === 'boolean') ? opts.notifyListeners : true;

      // creating the collection of fields having initial values run on them. This is needed
      // for error checking on date fields currently. This collection is cleared in
      // lm-field-base when firing initial value rules is complete
      if (opts.initialValue) {
        Object.keys(dvFields).forEach((key) => this._modelState.initialValueFields.push(key));
      }

      this._setFields(dvFields, { checkDirtyState: true });

      const source = (opts && opts.source) ? opts.source : undefined;
      const modelChangedEvent = new LMModelChangedEvent('update', source);

      if (notifyListeners) {
        this._modelChanged$.next(modelChangedEvent);
      }

      this.lmLoggerService.timeEnd('LMModel.setFields() ' + this.instanceId);

      observer.next(modelChangedEvent);
      observer.complete();
    });
  }

  public clearModelForRouteReuse() {
    let dvFields: LMFields = this._dataView.fields;
    Object.keys(dvFields).map((fieldId) => {
      let modelField: LMModelField = this.getField(fieldId);
      modelField.value = '';
    });
  }

  public clearModelState(): void {
    this._modelState.clearAll();
  }

  public clearFieldError(errorFieldId: string): boolean {
    const modelField = this.getField(errorFieldId);

    if (!modelField) {
      return false;
    }

    this.setError(errorFieldId, false);
    modelField.errorMessage.next('');

    return true;
  }

  public resetFieldErrors() {
    Object.keys(this._fields).map((fieldId) => {
      this.clearFieldError(fieldId);
    });
  }

  /**
   * Sets the field error onto the field
   *
   * @param errorFieldId
   * @param errorFieldMessage
   * @param applyErrorState - default true
   * @param indicates recursion level - initial zero
   * @returns - there is not a modelField or the modelField.errorMessage does not have any listeners
   */
  // NOTE: recursive method
  public setFieldError(errorFieldId: string, errorFieldMessage: string, applyErrorState?: boolean, level = 0): boolean {
    applyErrorState = (applyErrorState === undefined) ? true : applyErrorState;

    const modelField = this.getField(errorFieldId);

    if (!modelField) {
      return false;
    }

    if (applyErrorState) {
      this.setError(errorFieldId, true, errorFieldMessage);
    }

    if (modelField.errorMessage.observers.length > 0) {
      modelField.errorMessage.next(errorFieldMessage);
      return true;
    }

    // LMCLIENT-30413: if there are no observers and field is a group field, try to find the first member field
    // that has an observer to place the message; only going one level deep
    if (modelField.isCompoundField && level === 0) {
      for (let index = 0, len = modelField.members.length; index < len; index++) {
        const hasObservers = this.setFieldError(modelField.members[ index ], errorFieldMessage, applyErrorState, ++level);
        if (hasObservers) {
          return true;
        }
      }
    }

    // there are not any components listening, so caller needs to display dialog
    return false;
  }

  public clearInitialValueRules(): void {
    this._modelState.initialValueFields = [];
  }

  public getCache(autoApply?: boolean): LMModelCache {
    this.lmLoggerService.time({ id: 'LMModel.getCache() ' + this.instanceId });

    // need to iterate the modelFields to get their properties
    const modelFields = {};
    Object.keys(this._fields).forEach((key) => {
      const field = this.getField(key);

      if (field) {
        modelFields[ key ] = field.properties;
      }
    });

    // create the model cache object
    const modelCache: LMModelCache = {
      dirtyFields: this.state.dirtyFields,
      errorFields: this.state.errorFields,
      modelFields,
      autoApply:   autoApply || false
    };

    this.lmLoggerService.timeEnd('LMModel.getCache() ' + this.instanceId);
    return modelCache;
  }

  public applyCache(modelCache: LMModelCache) {
    this.lmLoggerService.time({ id: 'LMModel.applyCache() ' + this.instanceId });

    // create a local model with the state so changes can be applied
    const localModel = new LMModel(this.lmModelServiceAdapter, { formUrlInfo: this.formUrlInfo, formSpec: this.formSpec });
    localModel.populate(this._specFields);

    // set the model state collections
    localModel.state.cache = modelCache;

    // set the model field properties
    Object.keys(modelCache.modelFields).forEach((key) => {
      const field = localModel.getField(key);

      if (field) {
        field.properties = modelCache.modelFields[ key ];
      }
    });

    // apply the model state
    this.applyState(localModel);
  }

  public isNestedCompoundField(specField: LMSpecField): boolean {
    const modelField = this.getField(specField.bindId);
    return (modelField && modelField.isNestedCompoundField);
  }
}
